import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { FaPencil } from "react-icons/fa6";
import axios from 'axios';
import Alert from 'react-bootstrap/Alert';
import { Country, State } from 'country-state-city';
import './Details.css';

const Details = () => {
  const location = useLocation();
  const { accountId } = useParams();
  const { accountData = {} } = location.state || {};

  const countries = Country.getAllCountries();

  const [showAddress, setShowAddress] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);

  const [balanceData, setBalanceData] = useState({
    oneTo30Days: 0,
    thirtyOneTo60Days: 0,
    sixtyOneTo90Days: 0,
    ninetyPlusDays: 0,
    monthToDate: 0,
    amountOverdue: 0,
    amountDue: 0,
    lastStatementDate: null
  });

  const [selectedCountryIso, setSelectedCountryIso] = useState('');
  const [selectedShippingCountryIso, setSelectedShippingCountryIso] = useState('');
  const [selectedStateIso, setSelectedStateIso] = useState('');
  const [selectedShippingStateIso, setSelectedShippingStateIso] = useState('');

  const [formData, setFormData] = useState({
    account_name: accountData.account_name || '',
    address: accountData.address || '',
    address2: accountData.address2 || '',
    country: accountData.country || '',
    state: accountData.state || '',
    city: accountData.city || '',
    zip: accountData.zip || '',
    telephone: accountData.telephone || '',
    telephone2: accountData.telephone2 || '',
    fax: accountData.fax || '',
    email: accountData.email || '',
    website: accountData.website || '',
    time_zone: accountData.time_zone || '',
    status: accountData.status || '',
    account_type: accountData.account_type || '',
    ddxEnable: accountData.ddxEnable || false,
    quick_code: accountData.quick_code || '',
    deny_ddx_case_creation: accountData.deny_ddx_case_creation || false,
    shipping_address: accountData.shipping_address || '',
    shipping_address2: accountData.shipping_address2 || '',
    shipping_country: accountData.shipping_country || '',
    shipping_state: accountData.shipping_state || '',
    shipping_city: accountData.shipping_city || '',
    shipping_zip: accountData.shipping_zip || '',
    shipping_time_zone: accountData.shipping_time_zone || '',
  });

  const [accountHours, setAccountHours] = useState([
    { day: 'Monday', status: '9:00 AM - 5:00 PM' },
    { day: 'Tuesday', status: '9:00 AM - 5:00 PM' },
    { day: 'Wednesday', status: '9:00 AM - 5:00 PM' },
    { day: 'Thursday', status: '9:00 AM - 5:00 PM' },
    { day: 'Friday', status: '9:00 AM - 5:00 PM' },
    { day: 'Saturday', status: 'Closed' },
    { day: 'Sunday', status: 'Closed' }
  ]);

  // API Base URL (Live)
  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com/api';

  const fetchAccountBalance = async () => {
    if (!accountId) return;

    setLoading(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/statements/balance/${accountId}`
      );

      if (response.data.success && response.data.data) {
        setBalanceData(response.data.data);
      } else {
        console.log('No balance data found for account');
      }
    } catch (err) {
      console.error('Error fetching account balance:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountBalance();
  }, [accountId]);

  useEffect(() => {
    if (accountData.country) {
      const country = countries.find(
        c => c.name === accountData.country
      );

      if (country) {
        setSelectedCountryIso(country.isoCode);

        if (accountData.state) {
          const stateList = State.getStatesOfCountry(country.isoCode);

          const state = stateList.find(
            s => s.name === accountData.state
          );

          if (state) {
            setSelectedStateIso(state.isoCode);
          }
        }
      }
    }

    if (accountData.shipping_country) {
      const shippingCountry = countries.find(
        c => c.name === accountData.shipping_country
      );

      if (shippingCountry) {
        setSelectedShippingCountryIso(shippingCountry.isoCode);

        if (accountData.shipping_state) {
          const shippingStateList = State.getStatesOfCountry(
            shippingCountry.isoCode
          );

          const shippingState = shippingStateList.find(
            s => s.name === accountData.shipping_state
          );

          if (shippingState) {
            setSelectedShippingStateIso(shippingState.isoCode);
          }
        }
      }
    }
  }, [accountData]);

  const handleInputChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleTimeChange = (index, newStatus) => {
    const updatedHours = [...accountHours];
    updatedHours[index].status = newStatus;
    setAccountHours(updatedHours);
  };

  const handleCountryChange = (e) => {
    const iso = e.target.value;

    const country = countries.find(
      c => c.isoCode === iso
    );

    const countryName = country ? country.name : '';

    setSelectedCountryIso(iso);
    setSelectedStateIso('');

    setFormData({
      ...formData,
      country: countryName,
      state: ''
    });
  };

  const handleStateChange = (e) => {
    const iso = e.target.value;

    const stateList = selectedCountryIso
      ? State.getStatesOfCountry(selectedCountryIso)
      : [];

    const state = stateList.find(
      s => s.isoCode === iso
    );

    const stateName = state ? state.name : '';

    setSelectedStateIso(iso);

    setFormData({
      ...formData,
      state: stateName
    });
  };

  const handleShippingCountryChange = (e) => {
    const iso = e.target.value;

    const country = countries.find(
      c => c.isoCode === iso
    );

    const countryName = country ? country.name : '';

    setSelectedShippingCountryIso(iso);
    setSelectedShippingStateIso('');

    setFormData({
      ...formData,
      shipping_country: countryName,
      shipping_state: ''
    });
  };

  const handleShippingStateChange = (e) => {
    const iso = e.target.value;

    const stateList = selectedShippingCountryIso
      ? State.getStatesOfCountry(selectedShippingCountryIso)
      : [];

    const state = stateList.find(
      s => s.isoCode === iso
    );

    const stateName = state ? state.name : '';

    setSelectedShippingStateIso(iso);

    setFormData({
      ...formData,
      shipping_state: stateName
    });
  };

  const handleSaveClick = async () => {
    try {
      const updateData = {
        ...formData,
        accountHours,
      };

      const response = await axios.put(
        `${API_BASE_URL}/account/updateByAccountId/${accountId}`,
        updateData
      );

      setSuccess('Account updated successfully!');
      setError(null);
      setIsEditing(false);

      await fetchAccountBalance();

    } catch (error) {
      console.error('Error updating account:', error);

      setError(
        'Failed to update account. Please try again.'
      );

      setSuccess(null);
    }
  };

  const handleFieldClick = () => {
    setIsEditing(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const stateList = selectedCountryIso
    ? State.getStatesOfCountry(selectedCountryIso)
    : [];

  const shippingStateList = selectedShippingCountryIso
    ? State.getStatesOfCountry(selectedShippingCountryIso)
    : [];

  const handleRefreshBalance = () => {
    fetchAccountBalance();
  };

  return (
    <div
      className="details-main-container"
      style={{
        width: '99%',
        marginLeft: "10px"
      }}
    >

      <div className="row">

        <h2 style={{ color: "#004aad" }}>
          Details
        </h2>

        {error && (
          <Alert
            variant="danger"
            className="details-alert"
            style={{
              marginTop: '10px',
              width: 'fit-content'
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            variant="success"
            className="details-alert"
            style={{
              marginTop: '10px',
              width: 'fit-content'
            }}
          >
            {success}
          </Alert>
        )}

        {/* ================= NAME ================= */}
        <div
          className="details-form-section"
          style={{
            padding: "10px",
            backgroundColor: "#f4f4f4",
            borderTop: "3px solid #004aad"
          }}
        >
          <Row
            className="mb-3 details-form-row"
            style={{ alignItems: "center" }}
          >
            <Col xs={4} className="details-label-col">
              <b>
                Name <span style={{ color: 'red' }}>*</span>
              </b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.account_name}
                onChange={(e) =>
                  handleInputChange(
                    'account_name',
                    e.target.value
                  )
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= ADDRESS ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Address <span style={{ color: 'red' }}>*</span></b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) =>
                  handleInputChange('address', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= ADDRESS 2 ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Address 2</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.address2}
                onChange={(e) =>
                  handleInputChange('address2', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= COUNTRY ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Country <span style={{ color: 'red' }}>*</span></b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Select
                value={selectedCountryIso}
                onChange={handleCountryChange}
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              >
                <option value="">Select Country</option>

                {countries.map((country) => (
                  <option
                    key={country.isoCode}
                    value={country.isoCode}
                  >
                    {country.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* ================= STATE ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>State <span style={{ color: 'red' }}>*</span></b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Select
                value={selectedStateIso}
                onChange={handleStateChange}
                disabled={!isEditing || !selectedCountryIso}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              >
                <option value="">Select State</option>

                {stateList.map((state) => (
                  <option
                    key={state.isoCode}
                    value={state.isoCode}
                  >
                    {state.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </div>

        {/* ================= CITY ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>City</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.city}
                onChange={(e) =>
                  handleInputChange('city', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= ZIP ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>
                Zip / Postal Code <span style={{ color: 'red' }}>*</span>
              </b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.zip}
                onChange={(e) =>
                  handleInputChange('zip', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= TELEPHONE ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>
                Telephone <span style={{ color: 'red' }}>*</span>
              </b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.telephone}
                onChange={(e) =>
                  handleInputChange('telephone', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= TELEPHONE 2 ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Telephone 2</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.telephone2}
                onChange={(e) =>
                  handleInputChange('telephone2', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= FAX ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Fax</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.fax}
                onChange={(e) =>
                  handleInputChange('fax', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= EMAIL ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Email</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) =>
                  handleInputChange('email', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= WEBSITE ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Website</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.website}
                onChange={(e) =>
                  handleInputChange('website', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= TIME ZONE ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>
                Time Zone <span style={{ color: 'red' }}>*</span>
              </b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.time_zone}
                onChange={(e) =>
                  handleInputChange('time_zone', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= ACCOUNT STATUS ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>
                Account Status <span style={{ color: 'red' }}>*</span>
              </b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.status}
                onChange={(e) =>
                  handleInputChange('status', e.target.value)
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= DDX ENABLED ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>DDX Enabled</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Check
                type="checkbox"
                checked={formData.ddxEnable}
                onChange={(e) =>
                  handleInputChange(
                    'ddxEnable',
                    e.target.checked
                  )
                }
                disabled={!isEditing}
                onClick={handleFieldClick}
                aria-label="DDX Enabled"
              />
            </Col>
          </Row>
        </div>

        {/* ================= ACCOUNT TYPE ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>
                Account Type <span style={{ color: 'red' }}>*</span>
              </b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.account_type}
                onChange={(e) =>
                  handleInputChange(
                    'account_type',
                    e.target.value
                  )
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= QUICK CODE ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Quick Code</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Control
                type="text"
                value={formData.quick_code}
                onChange={(e) =>
                  handleInputChange(
                    'quick_code',
                    e.target.value
                  )
                }
                disabled={!isEditing}
                style={{
                  width: "200px",
                  backgroundColor: "white"
                }}
                placeholder="Enter Quick Code"
                onClick={handleFieldClick}
              />
            </Col>
          </Row>
        </div>

        {/* ================= DENY DDX ================= */}
        <div className="details-form-section" style={{
          padding: "10px",
          backgroundColor: "#f2f2f2"
        }}>
          <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
            <Col xs={4} className="details-label-col">
              <b>Deny DDX Case Creation</b>
            </Col>

            <Col xs={8} className="details-input-col">
              <Form.Check
                type="checkbox"
                checked={formData.deny_ddx_case_creation}
                onChange={(e) =>
                  handleInputChange(
                    'deny_ddx_case_creation',
                    e.target.checked
                  )
                }
                disabled={!isEditing}
                onClick={handleFieldClick}
                aria-label="Deny DDX Case Creation"
              />
            </Col>
          </Row>
        </div>

        <br />

        {/* ================= SHIPPING ADDRESS ================= */}
        <div>
          <div
            onClick={() =>
              setShowAddress(!showAddress)
            }
            className="shipping-heading"
            style={{
              color: "#004aad",
              padding: "10px",
              cursor: "pointer",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center"
            }}
          >
            <h2>
              <span style={{ marginRight: "3px" }}>
                Shipping Address
              </span>
            </h2>

            <span>
              {showAddress ? "▲" : "▼"}
            </span>
          </div>
        </div>

        <div
          className="shipping-section"
          style={{
            border: "1px solid #ccc",
            padding: "10px",
            marginBottom: "10px",
            borderTop: "3px solid #004aad",
            backgroundColor: "#f2f2f2"
          }}
        >

          {showAddress && (
            <div style={{
              padding: "10px",
              borderRadius: "5px"
            }}>

              {/* Shipping Address */}
              <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
                <Col xs={4} className="details-label-col">
                  <b>Address</b>
                </Col>

                <Col xs={8} className="details-input-col">
                  <Form.Control
                    type="text"
                    value={formData.shipping_address}
                    onChange={(e) =>
                      handleInputChange(
                        'shipping_address',
                        e.target.value
                      )
                    }
                    disabled={!isEditing}
                    style={{
                      width: "200px",
                      backgroundColor: "white"
                    }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              {/* Shipping Address 2 */}
              <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
                <Col xs={4} className="details-label-col">
                  <b>Address 2</b>
                </Col>

                <Col xs={8} className="details-input-col">
                  <Form.Control
                    type="text"
                    value={formData.shipping_address2}
                    onChange={(e) =>
                      handleInputChange(
                        'shipping_address2',
                        e.target.value
                      )
                    }
                    disabled={!isEditing}
                    style={{
                      width: "200px",
                      backgroundColor: "white"
                    }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              {/* Shipping Country */}
              <div style={{
                padding: "10px",
                backgroundColor: "#f2f2f2"
              }}>
                <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
                  <Col xs={4} className="details-label-col">
                    <b>
                      Country <span style={{ color: 'red' }}>*</span>
                    </b>
                  </Col>

                  <Col xs={8} className="details-input-col">
                    <Form.Select
                      value={selectedShippingCountryIso}
                      onChange={handleShippingCountryChange}
                      disabled={!isEditing}
                      style={{
                        width: "200px",
                        backgroundColor: "white"
                      }}
                      onClick={handleFieldClick}
                    >
                      <option value="">
                        Select Country
                      </option>

                      {countries.map((country) => (
                        <option
                          key={country.isoCode}
                          value={country.isoCode}
                        >
                          {country.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </div>

              {/* Shipping State */}
              <div style={{
                padding: "10px",
                backgroundColor: "#f2f2f2"
              }}>
                <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
                  <Col xs={4} className="details-label-col">
                    <b>
                      State <span style={{ color: 'red' }}>*</span>
                    </b>
                  </Col>

                  <Col xs={8} className="details-input-col">
                    <Form.Select
                      value={selectedShippingStateIso}
                      onChange={handleShippingStateChange}
                      disabled={
                        !isEditing ||
                        !selectedShippingCountryIso
                      }
                      style={{
                        width: "200px",
                        backgroundColor: "white"
                      }}
                      onClick={handleFieldClick}
                    >
                      <option value="">
                        Select State
                      </option>

                      {shippingStateList.map((state) => (
                        <option
                          key={state.isoCode}
                          value={state.isoCode}
                        >
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>
              </div>

              {/* Shipping City */}
              <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
                <Col xs={4} className="details-label-col">
                  <b>City</b>
                </Col>

                <Col xs={8} className="details-input-col">
                  <Form.Control
                    type="text"
                    value={formData.shipping_city}
                    onChange={(e) =>
                      handleInputChange(
                        'shipping_city',
                        e.target.value
                      )
                    }
                    disabled={!isEditing}
                    style={{
                      width: "200px",
                      backgroundColor: "white"
                    }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              {/* Shipping Zip */}
              <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
                <Col xs={4} className="details-label-col">
                  <b>Zip / Postal Code</b>
                </Col>

                <Col xs={8} className="details-input-col">
                  <Form.Control
                    type="text"
                    value={formData.shipping_zip}
                    onChange={(e) =>
                      handleInputChange(
                        'shipping_zip',
                        e.target.value
                      )
                    }
                    disabled={!isEditing}
                    style={{
                      width: "200px",
                      backgroundColor: "white"
                    }}
                    onClick={handleFieldClick}
                  />
                </Col>
              </Row>

              {/* Shipping Time Zone */}
              <div style={{
                padding: "10px",
                backgroundColor: "#f2f2f2"
              }}>
                <Row className="mb-3 details-form-row" style={{ alignItems: "center" }}>
                  <Col xs={4} className="details-label-col">
                    <b>
                      Time Zone <span style={{ color: 'red' }}>*</span>
                    </b>
                  </Col>

                  <Col xs={8} className="details-input-col">
                    <Form.Control
                      type="text"
                      value={formData.shipping_time_zone}
                      onChange={(e) =>
                        handleInputChange(
                          'shipping_time_zone',
                          e.target.value
                        )
                      }
                      disabled={!isEditing}
                      style={{
                        width: "200px",
                        backgroundColor: "white"
                      }}
                      onClick={handleFieldClick}
                    />
                  </Col>
                </Row>
              </div>

            </div>
          )}
        </div>

        {/* ================= HOURS ================= */}
        <div>

          <h5
            style={{
              backgroundColor: "white",
              color: "#004aad",
              padding: "10px 15px",
              fontWeight: "bold",
              marginBottom: "10px"
            }}
          >
            Account's Hours of Operation
          </h5>

          <div
            className="hours-section"
            style={{
              backgroundColor: "#f2f2f2",
              padding: "30px",
              borderTop: "3px solid #004aad",
              boxSizing: "border-box",
              margin: "0 auto"
            }}
          >

            {accountHours.map((item, index) => (
              <p
                key={index}
                style={{
                  margin: "10px 0",
                  fontSize: "14px"
                }}
              >
                <b>{item.day}:</b>{" "}

                {isEditing ? (
                  <input
                    type="text"
                    value={item.status}
                    onChange={(e) =>
                      handleTimeChange(
                        index,
                        e.target.value
                      )
                    }
                    style={{
                      marginLeft: "10px",
                      padding: "5px",
                      borderRadius: "4px",
                      border: "1px solid #ccc",
                      width: "200px",
                      maxWidth: "100%"
                    }}
                    onClick={handleFieldClick}
                  />
                ) : (
                  item.status
                )}
              </p>
            ))}

            {isEditing ? (
              <Button
                style={{
                  backgroundColor: "#004aad",
                  border: "none",
                  color: "#fff",
                  padding: "8px 16px",
                  marginTop: "15px",
                  borderRadius: "4px"
                }}
                onClick={handleSaveClick}
              >
                Save
              </Button>
            ) : (
              <Button
                style={{
                  backgroundColor: "#004aad",
                  border: "none",
                  color: "#fff",
                  padding: "8px 16px",
                  marginTop: "15px",
                  borderRadius: "4px"
                }}
                onClick={() =>
                  setIsEditing(true)
                }
              >
                <FaPencil /> Edit Hours
              </Button>
            )}

          </div>
        </div>

        {/* ================= BALANCE DETAILS ================= */}
        <div>

          <h5
            className="balance-heading"
            style={{
              color: "#004aad",
              padding: "5px",
              fontWeight: "bold",
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Balance Details

            {loading && (
              <Spinner
                animation="border"
                size="sm"
                style={{
                  marginLeft: '10px'
                }}
              />
            )}

            {!loading && (
              <Button
                variant="link"
                onClick={handleRefreshBalance}
                style={{
                  marginLeft: '10px',
                  padding: '0 5px',
                  color: '#004aad',
                  textDecoration: 'none'
                }}
                title="Refresh Balance"
              >
                ⟳
              </Button>
            )}

            {balanceData.lastStatementDate && (
              <small
                style={{
                  fontSize: '12px',
                  marginLeft: '10px',
                  color: '#666',
                  fontWeight: 'normal'
                }}
              >
                (Last updated:{" "}
                {new Date(
                  balanceData.lastStatementDate
                ).toLocaleDateString()})
              </small>
            )}
          </h5>

          <div
            className="balance-section"
            style={{
              backgroundColor: "#f2f2f2",
              padding: "20px",
              width: "100%",
              borderTop: "3px solid #004aad",
              boxSizing: 'border-box'
            }}
          >

            <p>
              <b>Month to Date:</b>{" "}
              {formatCurrency(balanceData.monthToDate)}
            </p>

            <p>
              <b>1-30 Days:</b>{" "}
              {formatCurrency(balanceData.oneTo30Days)}
            </p>

            <p>
              <b>31-60 Days:</b>{" "}
              {formatCurrency(balanceData.thirtyOneTo60Days)}
            </p>

            <p>
              <b>61-90 Days:</b>{" "}
              {formatCurrency(balanceData.sixtyOneTo90Days)}
            </p>

            <p>
              <b>91+ Days:</b>{" "}
              {formatCurrency(balanceData.ninetyPlusDays)}
            </p>

            <p>
              <b>Amount Overdue:</b>{" "}
              {formatCurrency(balanceData.amountOverdue)}
            </p>

            <p>
              <b>Amount Due:</b>{" "}
              {formatCurrency(balanceData.amountDue)}
            </p>

          </div>
        </div>

        <br />

        {/* ================= SAVE BUTTON ================= */}
        <div>
          <Button
            style={{
              backgroundColor: '#004aad',
              borderColor: '#004aad',
              marginTop: "10%"
            }}
            onClick={handleSaveClick}
          >
            <FaPencil
              style={{
                marginRight: '6px'
              }}
            />
            Save
          </Button>
        </div>

      </div>
    </div>
  );
};

export default Details;